import React, { useMemo, useState } from 'react';
import { Modal } from '../../../components/Modal';
import type { SubscriptionPlan } from '../types';

type AddSubscriptionModalProps = {
  plans: SubscriptionPlan[];
  loadingPlans: boolean;
  onCancel: () => void;
  onSubmit: (planId: string, startAt: Date | null, endAt: Date | null, reason: string) => void;
};

export function AddSubscriptionModal({ plans, loadingPlans, onCancel, onSubmit }: AddSubscriptionModalProps) {
  const [planId, setPlanId] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectablePlans = useMemo(
    () => plans.filter((plan) => plan.status !== 'inactive'),
    [plans]
  );

  const handleSubmit = async () => {
    if (!planId) {
      setError('Please select a subscription plan.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const startDate = startAt ? new Date(startAt) : null;
      const endDate = endAt ? new Date(endAt) : null;
      await onSubmit(planId, startDate, endDate, reason);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Add subscription grant"
      onClose={onCancel}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Adding…' : 'Add subscription'}
          </button>
        </>
      }
    >
      <form
        className="row g-3"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <div className="col-12">
          <label className="form-label" htmlFor="subscriptionPlan">
            Subscription plan
          </label>
          <select
            id="subscriptionPlan"
            className="form-select"
            value={planId}
            onChange={(event) => setPlanId(event.target.value)}
            disabled={loadingPlans}
            required
          >
            <option value="" disabled>
              {loadingPlans ? 'Loading plans…' : 'Select a plan'}
            </option>
            {selectablePlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name} ({plan.code})
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label" htmlFor="startAt">
            Start date (optional)
          </label>
          <input
            id="startAt"
            type="datetime-local"
            className="form-control"
            value={startAt}
            onChange={(event) => setStartAt(event.target.value)}
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label" htmlFor="endAt">
            End date (optional)
          </label>
          <input
            id="endAt"
            type="datetime-local"
            className="form-control"
            value={endAt}
            onChange={(event) => setEndAt(event.target.value)}
          />
        </div>
        <div className="col-12">
          <label className="form-label" htmlFor="reason">
            Reason (optional)
          </label>
          <textarea
            id="reason"
            className="form-control"
            rows={3}
            placeholder="Optional context for the audit log"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        {error && (
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
